/**
 * Code repository connection for the code-level security audit (security projects). A Project Admin connects
 * the app's GitHub / GitLab (cloud or self-hosted) / other Git repository with an optional read-only access
 * token; the runner clones it for the source-aware audit. The token is stored encrypted (ProjectCodeRepo) and
 * never returned by the API, written into a clone URL, or put in a queue job.
 */
import { z } from 'zod';

export const CODE_REPO_PROVIDERS = ['GITHUB', 'GITLAB', 'OTHER'] as const;
export type CodeRepoProvider = (typeof CODE_REPO_PROVIDERS)[number];

export const CODE_REPO_PROVIDER_LABEL: Record<CodeRepoProvider, string> = {
  GITHUB: 'GitHub',
  GITLAB: 'GitLab',
  OTHER: 'Other Git server',
};

/** Where to create a read-only token, shown next to the token field. */
export const CODE_REPO_TOKEN_HINT: Record<CodeRepoProvider, string> = {
  GITHUB: 'A fine-grained personal access token with read-only “Contents” access to this repository (GitHub › Settings › Developer settings).',
  GITLAB: 'A project or personal access token with the “read_repository” scope (GitLab › Settings › Access tokens). Works with self-hosted GitLab.',
  OTHER: 'A token or app password with read access to the repository, plus the user name your Git server expects.',
};

export const CODE_REPO_URL_PLACEHOLDER: Record<CodeRepoProvider, string> = {
  GITHUB: 'https://github.com/your-org/your-app',
  GITLAB: 'https://gitlab.com/your-group/your-app',
  OTHER: 'https://git.example.com/team/your-app.git',
};

/** Git branch names: no spaces, no "..", no leading "-" (it would read as a git option). */
const branchName = z
  .string()
  .trim()
  .max(250)
  .regex(/^(?!-)(?!.*\.\.)[A-Za-z0-9._\/-]+$/, 'Not a valid branch name');

/** An https address of a repository (no user/password inside: the token has its own field). */
export const repoUrlSchema = z
  .string()
  .trim()
  .max(2000)
  .url('Enter the repository address, e.g. https://github.com/your-org/your-app')
  .refine((u) => /^https:\/\//i.test(u), 'Use the https:// address of the repository')
  .refine((u) => {
    try {
      const url = new URL(u);
      return !url.username && !url.password && url.pathname.replace(/\/+$/, '').length > 1;
    } catch {
      return false;
    }
  }, 'Use the repository address without a user name or password (put the token in its own field)');

/** What the connect form sends. `token`: undefined keeps the stored one, '' removes it. */
export const codeRepoInputSchema = z.object({
  provider: z.enum(CODE_REPO_PROVIDERS),
  url: repoUrlSchema,
  /** Empty = the repository's default branch. */
  branch: branchName.optional().or(z.literal('')),
  /** OTHER only: the user name for the token (GitHub and GitLab need none). */
  username: z.string().trim().max(200).optional().or(z.literal('')),
  token: z.string().trim().max(1000).optional(),
});
export type CodeRepoInput = z.infer<typeof codeRepoInputSchema>;

/** What the API returns about a connected repository (never the token). */
export interface CodeRepoView {
  provider: CodeRepoProvider;
  url: string;
  /** Branch the audit reads; null = the default branch. */
  branch: string | null;
  username: string | null;
  hasToken: boolean;
  /** The repository's default branch, from the last successful connection check. */
  defaultBranch: string | null;
  checkedAt: string | null;
  updatedAt: string;
  updatedByName: string | null;
}

/** Guess the provider from the address (github.com, gitlab.* / *gitlab*), for the form's first suggestion. */
export function guessRepoProvider(url: string): CodeRepoProvider {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (host === 'github.com' || host.endsWith('.github.com')) return 'GITHUB';
    if (host.includes('gitlab')) return 'GITLAB';
  } catch {
    /* not a URL yet */
  }
  return 'OTHER';
}

/** The clone address: the https URL, trailing slashes removed, ".git" added (GitHub and GitLab accept both). */
export function cloneUrl(url: string): string {
  const u = new URL(url.trim());
  u.hash = '';
  u.search = '';
  let p = u.pathname.replace(/\/+$/, '');
  if (!p.toLowerCase().endsWith('.git')) p += '.git';
  u.pathname = p;
  return u.toString();
}

/** User name for HTTP Basic auth with a token: what each host expects (any non-empty name works for GitLab). */
export function gitAuthUser(provider: CodeRepoProvider, username?: string | null): string {
  if (provider === 'GITHUB') return 'x-access-token';
  if (provider === 'GITLAB') return 'oauth2';
  return username?.trim() || 'git';
}

/**
 * Reads the refs of a Git smart-HTTP `info/refs?service=git-upload-pack` answer (pkt-line format): the branch
 * names and the default branch (the HEAD symref capability). Works for GitHub, GitLab and any Git server.
 */
export function parseUploadPackRefs(body: string): { branches: string[]; defaultBranch: string | null } {
  const branches: string[] = [];
  let defaultBranch: string | null = null;
  let i = 0;
  while (i + 4 <= body.length) {
    const len = parseInt(body.slice(i, i + 4), 16);
    if (Number.isNaN(len)) break;
    if (len === 0) {
      i += 4; // flush-pkt
      continue;
    }
    const line = body.slice(i + 4, i + len).replace(/\n$/, '');
    i += len;
    if (line.startsWith('#')) continue;
    const [refPart, caps] = line.split('\0');
    const ref = refPart?.split(' ')[1];
    const sym = caps?.match(/symref=HEAD:refs\/heads\/(\S+)/);
    if (sym?.[1]) defaultBranch = sym[1];
    if (ref?.startsWith('refs/heads/')) branches.push(ref.slice('refs/heads/'.length));
  }
  return { branches, defaultBranch };
}
